import type { AppDispatch, RootState } from '@/store/store'
import { getRootFolder } from '@/user_files/folders/fetch/get-root-folder'
import { getFolderChildren } from '@/user_files/folders/fetch/get-folder-children'
import { createFolder } from '@/user_files/folders/fetch/post-create-folder'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectChildFolders, selectRootFolderIdForMatter } from '@/user_files/folders/store/selectors'

type GetOrCreateUserFilesUploadFolderIdParams = {
  dispatch: AppDispatch
  folderName: string
  getState: () => RootState
  matterId: string | null
}

const inFlightFolderRequests = new Map<string, Promise<string>>()

function getExistingUploadFolderId(state: RootState, matterId: string, folderName: string): string | null {
  const rootFolderId = selectRootFolderIdForMatter(state, { matterId })
  if (!rootFolderId) return null

  const uploadFolder = selectChildFolders(state, { parentId: rootFolderId }).find((folder) => folder.name === folderName)
  return uploadFolder?.id ?? null
}

async function getOrFetchRootFolderId(params: { dispatch: AppDispatch; getState: () => RootState; matterId: string }): Promise<string> {
  const existingRootFolderId = selectRootFolderIdForMatter(params.getState(), { matterId: params.matterId })
  if (existingRootFolderId) return existingRootFolderId

  const response = await getRootFolder(params.matterId)
  params.dispatch(UserFoldersActions.upsertFolder(response.folder))
  return response.folder.id
}

async function findUploadFolderOnServer(params: {
  dispatch: AppDispatch
  folderName: string
  getState: () => RootState
  rootFolderId: string
}): Promise<string | null> {
  const response = await getFolderChildren(params.rootFolderId)
  params.dispatch(UserFoldersActions.setFolderChildren({ parentId: params.rootFolderId, children: response.folders }))

  const uploadFolder = selectChildFolders(params.getState(), { parentId: params.rootFolderId }).find((folder) => folder.name === params.folderName)
  return uploadFolder?.id ?? null
}

async function getOrCreateUploadFolderId(params: GetOrCreateUserFilesUploadFolderIdParams & { matterId: string }): Promise<string> {
  const existingUploadFolderId = getExistingUploadFolderId(params.getState(), params.matterId, params.folderName)
  if (existingUploadFolderId) return existingUploadFolderId

  const rootFolderId = await getOrFetchRootFolderId(params)
  const serverUploadFolderId = await findUploadFolderOnServer({ ...params, rootFolderId })
  if (serverUploadFolderId) return serverUploadFolderId

  try {
    const folder = await createFolder({ parent_id: rootFolderId, name: params.folderName })
    params.dispatch(UserFoldersActions.upsertFolder(folder))
    return folder.id
  } catch (error) {
    const refetchedUploadFolderId = await findUploadFolderOnServer({ ...params, rootFolderId })
    if (refetchedUploadFolderId) return refetchedUploadFolderId
    throw error
  }
}

/**
 * Finds or creates the named Matter Files folder used by direct user-files uploads.
 * This runs on upload intent so normal page load does not prepare upload folders.
 */
export function getOrCreateUserFilesUploadFolderId(params: GetOrCreateUserFilesUploadFolderIdParams): Promise<string> {
  if (!params.matterId) {
    return Promise.reject(new Error('Select a matter before uploading to Matter Files.'))
  }

  const cacheKey = `${params.matterId}:${params.folderName}`
  const existingInFlightRequest = inFlightFolderRequests.get(cacheKey)
  if (existingInFlightRequest) return existingInFlightRequest

  const promise = getOrCreateUploadFolderId({ ...params, matterId: params.matterId }).finally(() => {
    inFlightFolderRequests.delete(cacheKey)
  })
  inFlightFolderRequests.set(cacheKey, promise)
  return promise
}
